// pages/TravelExpense/TravelExpenseForm.js
//
// Create-new-request / edit-draft form for Travel Expense Approval.
// Mirrors the legacy frmTravelExpense.vb layout:
//
//   Part 1 — Overview
//     Name of Trip, Primary Objective, Traveler's Name (read-only),
//     Leave Home Date, Return to Home Date,
//     Accompanying Employees (add/remove list)
//
//   Part 2 — Destinations
//     One row per company visited: Start Date, End Date, # Hours,
//     Nature of Visit, Company, City State Country, Contact, Goal.
//     Rows are added/removed inline.
//
//   Part 3 — Estimated Expenses
//     Airfare, Lodging, Meals, Entertainment, Car Rental, Personal Car,
//     Taxi, Misc + Misc description. Estimated Total auto-computes.
//
// Two save actions: "Save & Close" (Status=1 draft) and "Submit For
// Approval" (Status=2). The page works in two modes — /travel-expense/new
// renders blank; /travel-expense/:id/edit hydrates from GET edit-data.
//
// Routes:
//   /travel-expense/new        → blank create
//   /travel-expense/:id/edit   → edit a Draft (1) or a Denied (4) request
//                                (Go-live parity 2026-09-24: #9/#10 — a
//                                request awaiting approval is read-only)

import React, { useEffect, useMemo, useState } from "react";
import {
    Container, Card, CardBody, Row, Col, Input, Label, Button,
    Spinner, Badge, Alert, ListGroup, ListGroupItem,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardPageHeader } from "../../Components/Common/DashboardKit";
import api from "../../api";
import useAuth from "../Personal Attendance/useAuth";
import { toCamel } from "./travelCase";
import { NATURE_OF_VISIT } from "./travelStatus";

// ─── helpers ──────────────────────────────────────────────────────────────
const toIsoDate = (d) => {
    if (!d) return "";
    try {
        const dt = new Date(d);
        const y = dt.getFullYear(); const m = String(dt.getMonth() + 1).padStart(2, "0");
        const day = String(dt.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    } catch { return ""; }
};
const todayIso = () => toIsoDate(new Date());

const fmtMoney = (v) => {
    const n = Number(v || 0);
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};
// Numeric-only input handler — strips non-numeric / multi-decimal entries.
const cleanMoney = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/[^0-9.]/g, "");
    const parts = s.split(".");
    return parts.length > 1 ? parts[0] + "." + parts.slice(1).join("") : s;
};

// ─── blank-form initial state ─────────────────────────────────────────────
const blankDestination = () => ({
    startDate: todayIso(),
    endDate: todayIso(),
    numberOfHours: 0,
    natureOfVisit: "",
    company: "",
    cityStateCountry: "",
    contactPerson: "",
    goal: "",
});
const blankForm = () => ({
    nameOfTrip: "",
    primaryObjective: "",
    leaveHomeDate: todayIso(),
    returnHomeDate: todayIso(),
    accompanyingEmployeeIds: [],     // ints
    destinations: [],                // array of blankDestination()
    airfare: "0", lodging: "0", meals: "0", entertainment: "0",
    carRental: "0", personalCar: "0", taxi: "0", other: "0",
    otherNote: "",
});

// Go-live parity (2026-09-24): #12 — legacy TravelExpenseApproval.vb
// CheckValidityOfApproval (submit) + IsDestValid (each destination). The
// API applies the same rules; this only saves a round trip.
const validate = (form, submit) => {
    const problems = [];
    const add = (p) => { if (!problems.includes(p)) problems.push(p); };
    if (!submit) return problems;
    if (!form.nameOfTrip.trim()) add("Name of Trip is required.");
    if (!form.primaryObjective.trim()) add("Primary Objective is required.");
    if (!form.leaveHomeDate || !form.returnHomeDate) add("Leave Home Date and Return to Home Date are required.");
    else if (form.returnHomeDate < form.leaveHomeDate) add("Return to Home Date must be on or after Leave Home Date.");
    if (form.destinations.length === 0) add("At least one destination is required.");
    form.destinations.forEach((d) => {
        if (!d.startDate || !d.endDate || d.endDate < d.startDate) add("Destination end date must be on or after its start date.");
        if (!(parseInt(d.numberOfHours, 10) > 0)) add("Please enter the number of hours you plan on being at each location.");
        if (!NATURE_OF_VISIT.includes(d.natureOfVisit)) add("Nature of visit must be selected for each destination.");
        if (d.company.trim().length < 2) add("Company name must be entered for each destination.");
        if (d.cityStateCountry.trim().length < 5) add("City, State, and Country must be entered for each destination.");
        if (d.contactPerson.trim().length < 5) add("Contact Person must be entered for each destination.");
        if (d.goal.trim().length < 6) add("Goal must be entered for each destination.");
        if (form.leaveHomeDate && form.returnHomeDate) {
            if (d.startDate < form.leaveHomeDate || d.startDate > form.returnHomeDate)
                add("You have entered a destination start date that falls outside of the range of travel dates.");
            else if (d.endDate < form.leaveHomeDate || d.endDate > form.returnHomeDate)
                add("You have entered a destination end date that falls outside of the range of travel dates.");
        }
    });
    if (Number(form.other || 0) > 0 && !form.otherNote.trim()) add("Please enter a short note in 'Misc Expenses Description'.");
    return problems;
};

const TravelExpenseForm = () => {
    const { id } = useParams();           // undefined for /new
    const navigate = useNavigate();
    const auth = useAuth();

    const isEdit = Boolean(id);
    const [form, setForm] = useState(blankForm);
    const [loading, setLoading] = useState(isEdit);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [problems, setProblems] = useState([]);
    // Go-live parity (2026-09-24): #9/#10 — status of the request being edited;
    // only Draft (1) and Denied (4) can be changed.
    const [loadedStatus, setLoadedStatus] = useState(null);
    const locked = isEdit && loadedStatus != null && loadedStatus !== 1 && loadedStatus !== 4;

    // Go-live parity (2026-09-24): #13 — accompanying employees are picked by
    // name from the active employee list (was a raw empId box).
    const [employeeInput, setEmployeeInput] = useState("");
    const [employeeHits, setEmployeeHits] = useState([]);
    const [employeeNames, setEmployeeNames] = useState({});   // empId → name

    // ─── load existing draft for edit mode ───────────────────────────────
    useEffect(() => {
        if (!isEdit) return;
        let alive = true;
        setLoading(true); setError(null);
        api.get(`TravelExpense/${id}/edit-data`)
            .then(({ data: raw }) => {
                if (!alive) return;
                // Go-live parity (2026-09-24): camelCase response (was
                // data.Trip / data.Destinations / data.EmployeeIds only).
                const data = toCamel(raw);
                const t = data?.trip ?? {};
                const dests = (data?.destinations ?? []).map((d) => ({
                    startDate: toIsoDate(d.startDate),
                    endDate: toIsoDate(d.endDate),
                    numberOfHours: d.numberOfHours ?? 0,
                    natureOfVisit: d.natureOfVisit ?? "",
                    company: d.company ?? "",
                    cityStateCountry: d.cityStateCountry ?? "",
                    contactPerson: d.contactPerson ?? "",
                    goal: d.goal ?? "",
                }));
                // Exclude the requester from the accompanying list (they're
                // always the trip owner — we manage that server-side).
                const meInt = parseInt(auth.empId ?? auth.uid ?? "0", 10);
                const accompanying = (data?.employeeIds ?? []).filter((e) => e !== meInt);
                const names = {};
                (data?.employees ?? []).forEach((e) => { names[e.empId] = e.name; });
                setEmployeeNames(names);
                setLoadedStatus(t.status ?? null);
                setForm({
                    nameOfTrip: t.nameOfTrip ?? "",
                    primaryObjective: t.primaryObjective ?? "",
                    leaveHomeDate: toIsoDate(t.leaveHomeDate) || todayIso(),
                    returnHomeDate: toIsoDate(t.returnHomeDate) || todayIso(),
                    accompanyingEmployeeIds: accompanying,
                    destinations: dests,
                    airfare: String(t.airfare ?? 0),
                    lodging: String(t.lodging ?? 0),
                    meals: String(t.meals ?? 0),
                    entertainment: String(t.entertainment ?? 0),
                    carRental: String(t.carRental ?? 0),
                    personalCar: String(t.personalCar ?? 0),
                    taxi: String(t.taxi ?? 0),
                    other: String(t.other ?? 0),
                    otherNote: t.otherNote ?? "",
                });
            })
            .catch((e) => { if (alive) setError(e?.response?.data?.error || e?.message || "Failed to load draft"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [id, isEdit, auth.empId, auth.uid]);

    // ─── computed total ──────────────────────────────────────────────────
    const total = useMemo(() => {
        const sum = ["airfare", "lodging", "meals", "entertainment", "carRental", "personalCar", "taxi", "other"]
            .reduce((acc, k) => acc + Number(form[k] || 0), 0);
        return sum;
    }, [form]);

    // ─── small helpers ────────────────────────────────────────────────────
    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setMoney = (k, v) => setField(k, cleanMoney(v));

    const addDestination = () => setForm((f) => ({ ...f, destinations: [...f.destinations, blankDestination()] }));
    const removeDestination = (i) => setForm((f) => ({ ...f, destinations: f.destinations.filter((_, idx) => idx !== i) }));
    const setDest = (i, k, v) => setForm((f) => ({
        ...f,
        destinations: f.destinations.map((d, idx) => idx === i ? { ...d, [k]: v } : d),
    }));

    // Name / empId search against the active employee list (debounced).
    useEffect(() => {
        const q = employeeInput.trim();
        if (q.length < 2) { setEmployeeHits([]); return; }
        let alive = true;
        const h = setTimeout(() => {
            api.get("TravelExpense/employees", { params: { q } })
                .then(({ data }) => { if (alive) setEmployeeHits(toCamel(data)?.items ?? []); })
                .catch(() => { if (alive) setEmployeeHits([]); });
        }, 250);
        return () => { alive = false; clearTimeout(h); };
    }, [employeeInput]);

    const addEmployee = (hit) => {
        const n = hit?.empId;
        if (!Number.isFinite(n) || n <= 0) return;
        const meInt = parseInt(auth.empId ?? auth.uid ?? "0", 10);
        setEmployeeInput(""); setEmployeeHits([]);
        if (n === meInt || form.accompanyingEmployeeIds.includes(n)) return;
        setEmployeeNames((m) => ({ ...m, [n]: hit.name }));
        setForm((f) => ({ ...f, accompanyingEmployeeIds: [...f.accompanyingEmployeeIds, n] }));
    };
    const removeEmployee = (e) =>
        setForm((f) => ({ ...f, accompanyingEmployeeIds: f.accompanyingEmployeeIds.filter((x) => x !== e) }));

    // ─── submit handler ───────────────────────────────────────────────────
    const save = async (submitForApproval) => {
        setError(null); setProblems([]);
        // Go-live parity (2026-09-24): #12 — legacy checks before submitting.
        const found = validate(form, submitForApproval);
        if (found.length > 0) {
            setError("There is an error with your submission. Please double check your entry and try again.");
            setProblems(found);
            window.scrollTo(0, 0);
            return;
        }
        if (submitForApproval && total === 0
            && !window.confirm("You have not entered any expenses for this request. An estimated expense is required to help approve your request. Are you sure you wish to continue?")) {
            return;
        }
        setBusy(true);
        const payload = {
            nameOfTrip: form.nameOfTrip,
            primaryObjective: form.primaryObjective,
            leaveHomeDate: form.leaveHomeDate || null,
            returnHomeDate: form.returnHomeDate || null,
            accompanyingEmployeeIds: form.accompanyingEmployeeIds,
            destinations: form.destinations.map((d) => ({
                startDate: d.startDate,
                endDate: d.endDate,
                numberOfHours: Number(d.numberOfHours) || 0,
                natureOfVisit: d.natureOfVisit,
                company: d.company,
                cityStateCountry: d.cityStateCountry,
                contactPerson: d.contactPerson,
                goal: d.goal,
            })),
            airfare: Number(form.airfare) || 0,
            lodging: Number(form.lodging) || 0,
            meals: Number(form.meals) || 0,
            entertainment: Number(form.entertainment) || 0,
            carRental: Number(form.carRental) || 0,
            personalCar: Number(form.personalCar) || 0,
            taxi: Number(form.taxi) || 0,
            other: Number(form.other) || 0,
            otherNote: form.otherNote,
            submitForApproval,
        };
        try {
            if (isEdit) await api.put(`TravelExpense/${id}`, payload);
            else        await api.post("TravelExpense", payload);
            navigate("/travel-expense");
        } catch (e) {
            // Backend error envelope: { error, problems? }. Go-live parity
            // (2026-09-24): #32 — the API no longer returns exception text;
            // validation failures list each problem (#12).
            const d = e?.response?.data;
            setError((typeof d === "string" ? d : d?.error) || e?.message || "Save failed");
            setProblems(Array.isArray(d?.problems) ? d.problems : []);
            setBusy(false);
            window.scrollTo(0, 0);
        }
    };

    if (loading) return (<div className="page-content"><Container fluid><div className="text-center py-5"><Spinner color="primary" /></div></Container></div>);
    // Go-live parity (2026-09-24): #10 — a request awaiting approval (or
    // decided) can no longer be edited; send the owner to the read-only view.
    if (locked) return (
        <div className="page-content"><Container fluid>
            <Alert color="warning" className="mt-3">
                This request is {loadedStatus === 2 ? "awaiting approval" : "no longer a draft"} and can't be edited.{" "}
                <Button color="link" className="p-0 align-baseline" onClick={() => navigate(`/travel-expense/${id}`)}>Open the request</Button>
            </Alert>
        </Container></div>
    );
    const isResubmit = isEdit && loadedStatus === 4;
    const minLeave = isEdit ? undefined : todayIso();   // legacy date pickers: new requests start today or later

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title={isEdit ? `Edit Trip #${id}` : "New Travel Expense Request"}
                    breadcrumb={[{ label: "Travel Expense", to: "/workflows?wf=travel" }, { label: isEdit ? `Edit Trip #${id}` : "New Travel Expense Request" }]}
                    subtitle="Submit a request for travel expense approval"
                    breadcrumbItems={[
                        { label: "Travel Expense", href: "/travel-expense" },
                        { label: isEdit ? "Edit" : "New" },
                    ]}
                />
                {error && (
                    <Alert color="danger" className="mt-2">
                        {error}
                        {problems.length > 0 && (
                            <ul className="mb-0 mt-2">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
                        )}
                    </Alert>
                )}
                {isResubmit && (
                    <Alert color="info" className="mt-2">
                        This request was denied. Correct it and click "Resubmit For Approval" to send it back to your supervisor.
                    </Alert>
                )}

                {/* ─── PART 1 — OVERVIEW ─────────────────────────────── */}
                <Card className="mt-3">
                    <CardBody>
                        <h5 className="text-uppercase text-muted mb-3" style={{ letterSpacing: 1 }}>Part 1 — Overview</h5>
                        <Row className="g-3">
                            <Col md={8}>
                                <Label>Name of Trip</Label>
                                <Input value={form.nameOfTrip} onChange={(e) => setField("nameOfTrip", e.target.value)} disabled={busy} />
                            </Col>
                            <Col md={4}>
                                <Label>Traveler</Label>
                                <Input value={auth.name || auth.empId || ""} disabled />
                            </Col>
                            <Col md={12}>
                                <Label>Primary Objective</Label>
                                <Input type="textarea" rows={2} value={form.primaryObjective}
                                    onChange={(e) => setField("primaryObjective", e.target.value)} disabled={busy} />
                            </Col>
                            <Col md={3}>
                                <Label>Leave Home Date</Label>
                                <Input type="date" value={form.leaveHomeDate} min={minLeave}
                                    onChange={(e) => setField("leaveHomeDate", e.target.value)} disabled={busy} />
                            </Col>
                            <Col md={3}>
                                <Label>Return to Home Date</Label>
                                <Input type="date" value={form.returnHomeDate} min={form.leaveHomeDate || minLeave}
                                    onChange={(e) => setField("returnHomeDate", e.target.value)} disabled={busy} />
                            </Col>
                            <Col md={6}>
                                <Label>Accompanying Employees</Label>
                                <div className="position-relative">
                                    <Input value={employeeInput}
                                        placeholder="Type a name or employee ID"
                                        onChange={(e) => setEmployeeInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") { e.preventDefault(); if (employeeHits.length > 0) addEmployee(employeeHits[0]); }
                                        }}
                                        disabled={busy} autoComplete="off" />
                                    {employeeHits.length > 0 && (
                                        <ListGroup className="position-absolute w-100 shadow" style={{ zIndex: 20, maxHeight: 240, overflowY: "auto" }}>
                                            {employeeHits.map((h) => (
                                                <ListGroupItem key={h.empId} action tag="button" type="button"
                                                    onClick={() => addEmployee(h)}>
                                                    {h.name} <span className="text-muted small">({h.empId})</span>
                                                </ListGroupItem>
                                            ))}
                                        </ListGroup>
                                    )}
                                </div>
                                {form.accompanyingEmployeeIds.length > 0 && (
                                    <div className="mt-2">
                                        {form.accompanyingEmployeeIds.map((e) => (
                                            <Badge key={e} color="info" pill className="me-2 mb-1" style={{ cursor: "pointer" }}
                                                onClick={() => !busy && removeEmployee(e)} title="Click to remove">
                                                {employeeNames[e] || e} ×
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* ─── PART 2 — DESTINATIONS ──────────────────────────── */}
                <Card className="mt-3">
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="text-uppercase text-muted mb-0" style={{ letterSpacing: 1 }}>Part 2 — Destinations</h5>
                            <Button color="primary" outline size="sm" onClick={addDestination} disabled={busy}>+ Add Destination</Button>
                        </div>

                        {form.destinations.length === 0 ? (
                            <div className="text-muted text-center py-3">
                                No destinations yet. Click "Add Destination" to add one row per company you'll visit.
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-bordered table-sm mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 130 }}>Start Date</th>
                                            <th style={{ width: 130 }}>End Date</th>
                                            <th style={{ width: 80 }}># Hours</th>
                                            <th>Nature of Visit</th>
                                            <th>Company</th>
                                            <th>City, State, Country</th>
                                            <th>Contact</th>
                                            <th>Goal</th>
                                            <th style={{ width: 40 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form.destinations.map((d, i) => (
                                            <tr key={i}>
                                                {/* Go-live parity (2026-09-24): #12 dates inside the trip, fixed
                                                    Nature of Visit list; #30 column sizes as maxLength. */}
                                                <td><Input type="date" bsSize="sm" value={d.startDate}
                                                    min={form.leaveHomeDate || undefined} max={form.returnHomeDate || undefined}
                                                    onChange={(e) => setDest(i, "startDate", e.target.value)} disabled={busy} /></td>
                                                <td><Input type="date" bsSize="sm" value={d.endDate}
                                                    min={d.startDate || form.leaveHomeDate || undefined} max={form.returnHomeDate || undefined}
                                                    onChange={(e) => setDest(i, "endDate", e.target.value)} disabled={busy} /></td>
                                                <td><Input type="number" bsSize="sm" min="1" step="1" value={d.numberOfHours}
                                                    onChange={(e) => setDest(i, "numberOfHours", e.target.value)} disabled={busy} /></td>
                                                <td><Input type="select" bsSize="sm" value={NATURE_OF_VISIT.includes(d.natureOfVisit) ? d.natureOfVisit : ""}
                                                    onChange={(e) => setDest(i, "natureOfVisit", e.target.value)} disabled={busy}>
                                                    <option value="">{d.natureOfVisit && !NATURE_OF_VISIT.includes(d.natureOfVisit) ? `(${d.natureOfVisit}) — choose` : "Select…"}</option>
                                                    {NATURE_OF_VISIT.map((n) => <option key={n} value={n}>{n}</option>)}
                                                </Input></td>
                                                <td><Input bsSize="sm" value={d.company} maxLength={255}
                                                    onChange={(e) => setDest(i, "company", e.target.value)} disabled={busy} /></td>
                                                <td><Input bsSize="sm" value={d.cityStateCountry} maxLength={512}
                                                    onChange={(e) => setDest(i, "cityStateCountry", e.target.value)} disabled={busy} /></td>
                                                <td><Input bsSize="sm" value={d.contactPerson} maxLength={255}
                                                    onChange={(e) => setDest(i, "contactPerson", e.target.value)} disabled={busy} /></td>
                                                <td><Input bsSize="sm" value={d.goal}
                                                    onChange={(e) => setDest(i, "goal", e.target.value)} disabled={busy} /></td>
                                                <td className="text-center">
                                                    <Button color="link" size="sm" className="text-danger p-0"
                                                        onClick={() => removeDestination(i)} disabled={busy}>×</Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* ─── PART 3 — ESTIMATED EXPENSES ─────────────────────── */}
                <Card className="mt-3">
                    <CardBody>
                        <h5 className="text-uppercase text-muted mb-3" style={{ letterSpacing: 1 }}>Part 3 — Estimated Expenses (for the entire group)</h5>
                        <Row className="g-3">
                            {[
                                ["airfare", "Airfare"],
                                ["lodging", "Lodging"],
                                ["meals", "Meals"],
                                ["entertainment", "Entertainment"],
                                ["carRental", "Car Rental"],
                                ["personalCar", "Personal Car"],
                                ["taxi", "Taxi"],
                                ["other", "Misc Expenses"],
                            ].map(([k, label]) => (
                                <Col md={3} key={k}>
                                    <Label>{label}</Label>
                                    <div className="d-flex align-items-center">
                                        <span className="me-1">$</span>
                                        <Input bsSize="sm" inputMode="decimal" value={form[k]}
                                            onChange={(e) => setMoney(k, e.target.value)} disabled={busy} />
                                    </div>
                                </Col>
                            ))}
                            <Col md={12}>
                                <Label>Misc Expenses Description</Label>
                                <Input value={form.otherNote}
                                    onChange={(e) => setField("otherNote", e.target.value)} disabled={busy} />
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-3 pt-3 border-top">
                            <h5 className="mb-0">Estimated Total:&nbsp;<strong>{fmtMoney(total)}</strong></h5>
                        </div>
                    </CardBody>
                </Card>

                {/* ─── action bar ───────────────────────────────────────── */}
                <div className="mt-3 d-flex justify-content-between">
                    <Button color="link" onClick={() => navigate("/travel-expense")} disabled={busy}>Cancel</Button>
                    <div className="d-flex gap-2">
                        <Button color="secondary" outline onClick={() => save(false)} disabled={busy}>
                            {busy ? <Spinner size="sm" /> : "Save & Close (Draft)"}
                        </Button>
                        <Button color="primary" onClick={() => save(true)} disabled={busy}>
                            {busy ? <Spinner size="sm" /> : isResubmit ? "Resubmit For Approval" : "Submit For Approval"}
                        </Button>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default TravelExpenseForm;
